/**
 * The email builder's variable palette, built from a form **response** rather
 * than a form definition.
 *
 * A definition lists what a form could collect; a response shows what it
 * actually did, with the values to prove it — which is what tells an author
 * whether a token will read well, and what makes an object-valued field's
 * display text visible at all.
 *
 * Two hosts supply the response differently and share everything after the
 * fetch:
 *   - the standalone builder passes the latest stored
 *     `form_templates_responses` row;
 *   - the form-builder drawer passes the live preview object the form builder
 *     keeps in `formTemplateResponseStore`, which reflects the draft being
 *     edited — including fields added moments ago that no submitted row could
 *     contain.
 * Both are the same `{formId: {pageId: {key: value}}}` shape, so
 * `buildResponseVariables` is written once and handed an object.
 */

import Constants from "src/utils/constants";
import Helper from "src/utils/helper";
import { useFormTemplateResponseStore } from "src/stores/formTemplateResponseStore";

/**
 * `{ [fieldId]: field }` for every field across a form's pages.
 *
 * Deliberately not `Helper.flattenFormFields`. That walk is shared with the
 * dashboard column picker (PHO-913) and returns a fixed, narrow shape — label,
 * type, location — which must not grow to carry lookup config. Classifying a
 * response value needs the raw field: `optionValue`, `optionLabel`,
 * `options_ref` and `options_ref_config`.
 *
 * Keyed by field id rather than by location, because a response stores values
 * under bare field ids and the location cannot be recovered from it. A field id
 * on two pages resolves to the last seen — acceptable, since the lookup config
 * that matters here belongs to the field, not to where it sits.
 *
 * @param {Object} pagesById `{ [pageId]: form_pages row }`
 * @returns {Object} `{ [fieldId]: field }`
 */
export function buildFieldMap(pagesById) {
  const map = {};
  for (const page of Object.values(pagesById ?? {})) {
    for (const row of page?.fields ?? []) {
      for (const field of row?.group ?? []) {
        if (field?.id) map[field.id] = field;
      }
    }
  }
  return map;
}

/**
 * Whether a form has anything to build an email from — at least one field that
 * holds a value. Layout-only types (`FIELD_TYPES_NO_DATA`) don't count, so a
 * form made only of titles and text blocks reads as having no fields.
 *
 * @param {Object} fieldMap from `buildFieldMap`
 * @returns {boolean}
 */
export function hasDataFields(fieldMap) {
  return Object.values(fieldMap ?? {}).some(
    (field) =>
      !Constants.enums.FIELD_TYPES_NO_DATA.includes(field?.fieldObj?.field_type)
  );
}

/**
 * The token an author should insert for a variable.
 *
 * A scalar inserts bare — `$(key)` — and resolves to its own value. An
 * object-valued field would render as `[object Object]` that way, so it
 * inserts a sub-path instead: `$(key).label`. That syntax already works at send
 * time; `findDynamicKeyInExpression` matches `/\$\(([^)]+)\)(\.\w+)?/` and
 * indexes into an object value, so nothing new is needed to resolve it.
 *
 * The property is the field's own `optionLabel` where the sample actually has
 * it, then `label`, then `name`, then the first string property on the object.
 * The fallbacks matter: `optionLabel` describes what the *picker* displayed,
 * and a stored row does not always carry that column — inserting a property the
 * object lacks yields an empty string at send time, silently.
 */
function tokenFor(key, type, value, optionLabel) {
  if (type !== Constants.enums.EMAIL_VARIABLE_TYPES.OBJECT) return `$(${key})`;

  const candidates = [optionLabel, "label", "name"].filter(Boolean);
  const property =
    candidates.find((name) => Helper.isStringValueExist(value?.[name])) ??
    Object.keys(value ?? {}).find(
      (name) => typeof value[name] === "string" && value[name].trim()
    );

  return property ? `$(${key}).${property}` : `$(${key})`;
}

/**
 * Whether a field stores an id whose display label lives elsewhere — a
 * pre-defined list or a table, with `optionValue` picking the id out of the
 * chosen row. Shared by the palette's `LOOKUP` classification and the send-time
 * delivery guard, so both agree on what a lookup is.
 */
export function isLookupField(field) {
  return Boolean(
    (field?.options_ref || field?.options_ref_config) && field?.optionValue
  );
}

/**
 * Turns a response object into the variable list the palette offers.
 *
 * Every key becomes an entry. Keys with no field definition are the response's
 * own metadata (`submission_status`, `submiited_by_name` and friends) — as
 * usable in an email as any field value, so they are kept and labelled from the
 * key itself rather than filtered against a maintained list, which would drop a
 * new metadata key the day it appeared.
 *
 * @param {Object} response `{formId: {pageId: {key: value}}}`
 * @param {Object} [fieldMap] from `buildFieldMap`
 * @returns {Array<Object>} one entry per key, each with a `sample` and a `type`
 */
export function buildResponseVariables(response, fieldMap = {}) {
  const out = [];
  const seen = new Set();

  const addEntry = (key, value, pageId) => {
    if (!key || seen.has(key)) return;
    seen.add(key);

    const field = fieldMap[key];
    const optionValue = field?.optionValue ?? null;
    const optionLabel = field?.optionLabel ?? "label";
    const optionsRef = field?.options_ref ?? null;
    const optionsRefConfig = field?.options_ref_config ?? null;

    // Three cases, and the field definition is what separates them:
    //   an object value already carries its own display text — the whole source
    //     row was stored, i.e. the field's optionValue was null;
    //   a bare value on a field that draws from a pre-defined list or a table is
    //     an id whose label lives elsewhere, and renders as a raw id until it is
    //     resolved at send time;
    //   everything else is ready to read.
    let type = Constants.enums.EMAIL_VARIABLE_TYPES.SCALAR;
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      type = Constants.enums.EMAIL_VARIABLE_TYPES.OBJECT;
    } else if (Helper.isStringValueExist(value) && isLookupField(field)) {
      type = Constants.enums.EMAIL_VARIABLE_TYPES.LOOKUP;
    }

    out.push({
      key,
      token: tokenFor(key, type, value, optionLabel),
      label: field?.label || Helper.formatEnumLabel(key),
      fieldType: field?.fieldObj?.field_type ?? null,
      isMeta: !field,
      pageId: pageId ?? null,
      type,
      sample: value,
      optionLabel,
      optionValue,
      optionsRef,
      optionsRefConfig
    });
  };

  // `{formId: {pageId: {key: value}}}`, but tolerant of a response stored one
  // level shallower — the live preview object can be in that state before a
  // page has been interacted with.
  for (const formLevel of Object.values(response ?? {})) {
    if (!formLevel || typeof formLevel !== "object") continue;
    for (const [pageId, pageLevel] of Object.entries(formLevel)) {
      const isPage =
        pageLevel && typeof pageLevel === "object" && !Array.isArray(pageLevel);
      if (isPage) {
        for (const [key, value] of Object.entries(pageLevel)) {
          addEntry(key, value, pageId);
        }
      } else {
        addEntry(pageId, pageLevel, null);
      }
    }
  }

  return out;
}

/**
 * Turns a pasted JSON object into the same variable list a response produces.
 *
 * For alerts with no form behind them: the author supplies an example of the
 * object the sender will pass, and its keys become the palette. Patrol Weekly
 * Report QC is the case this exists for — it is dispatched imperatively with a
 * hand-built object rather than by a form submission.
 *
 * Only the top level is offered. `findAndSetKeyInObject` is a recursive key
 * search, so a nested key would also resolve as a bare `$(key)` — but listing
 * both `$(parent).child` and `$(child)` for one value is the ambiguity the
 * one-source-per-template rule exists to avoid, so nested objects are offered
 * as a single entry with a sub-path token, exactly as an object-valued form
 * field is.
 *
 * No `LOOKUP` classification: there is no field definition behind these keys,
 * so there is nothing to resolve an id against. P2.10's send-time lookup
 * applies to form sources only — an author supplying their own object supplies
 * readable values with it.
 *
 * @param {Object} sample the parsed object
 * @returns {Array<Object>} entries in the same shape `buildResponseVariables` returns
 */
export function buildJsonVariables(sample) {
  if (!sample || typeof sample !== "object" || Array.isArray(sample)) return [];

  return Object.entries(sample).map(([key, value]) => {
    const type =
      value !== null && typeof value === "object" && !Array.isArray(value)
        ? Constants.enums.EMAIL_VARIABLE_TYPES.OBJECT
        : Constants.enums.EMAIL_VARIABLE_TYPES.SCALAR;

    return {
      key,
      token: tokenFor(key, type, value, null),
      label: Helper.formatEnumLabel(key),
      fieldType: null,
      isMeta: false,
      pageId: null,
      type,
      sample: value,
      optionLabel: null,
      optionValue: null,
      optionsRef: null,
      optionsRefConfig: null
    };
  });
}

/**
 * Turns chosen table columns into the same variable list the other sources
 * produce.
 *
 * No sample values: nothing here reads a row — the schema browser returns names
 * and types only — so entries show the column's data type where the other
 * sources show real data. Adding samples would mean selecting from an arbitrary
 * table, which is a separate decision with a different risk profile.
 *
 * All scalar. There is no field definition behind a column, so there is nothing
 * to resolve an id against, exactly as with the JSON source.
 *
 * @param {Array<{name: string, dataType: string}>} columns the chosen columns
 * @returns {Array<Object>} entries in the same shape as the other builders
 */
export function buildTableVariables(columns) {
  return (columns ?? [])
    .filter((column) => column?.name)
    .map((column) => ({
      key: column.name,
      token: `$(${column.name})`,
      label: Helper.formatEnumLabel(column.name),
      fieldType: column.dataType ?? null,
      isMeta: false,
      pageId: null,
      type: Constants.enums.EMAIL_VARIABLE_TYPES.SCALAR,
      sample: null,
      optionLabel: null,
      optionValue: null,
      optionsRef: null,
      optionsRefConfig: null
    }));
}

/**
 * `{ [pageId]: tag }` from a template's page refs, including nested list pages.
 *
 * A response records which page a value came from, but only by id. The palette
 * groups by page, so it needs the tags — which live on the refs in
 * `form_templates.pages`, not on the `form_pages` rows themselves.
 *
 * @param {Object} template form_templates row
 * @returns {Object} `{ [pageId]: tag }`
 */
export function buildPageTagMap(template) {
  const tags = {};
  const walk = (refs) => {
    for (const pageRef of refs ?? []) {
      if (pageRef?.page_id && !tags[pageRef.page_id]) {
        tags[pageRef.page_id] = pageRef.tag || "";
      }
      if (Array.isArray(pageRef?.list_items)) walk(pageRef.list_items);
    }
  };
  walk(template?.pages);
  return tags;
}

function mockFieldValue(field) {
  switch (field?.fieldObj?.field_type) {
    case Constants.enums.FIELD_TYPES.INPUT_NUMBER:
    case Constants.enums.FIELD_TYPES.CALCULATED:
    case Constants.enums.FIELD_TYPES.AUTO_INCREMENT:
      return 123;
    case Constants.enums.FIELD_TYPES.INPUT_DATE_TIME_PICKER:
      return new Date().toISOString();
    case Constants.enums.FIELD_TYPES.TOGGLE:
    case Constants.enums.FIELD_TYPES.CHECKBOX:
      return true;
    case Constants.enums.FIELD_TYPES.RADIO_GROUP:
      return field.options?.[0]?.value || "option1";
    case Constants.enums.FIELD_TYPES.DROPDOWN:
      return field.options?.[0] || { label: "Option 1", value: "option1" };
    case Constants.enums.FIELD_TYPES.LOCATION_PICKER:
      return { latitude: 53.5, longitude: -113.5 };
    default:
      return "PLACEHOLDER";
  }
}

function isMockedField(field) {
  const type = field?.fieldObj?.field_type;
  return !(
    Constants.enums.FIELD_TYPES_NO_DATA.includes(type) ||
    type === Constants.enums.FIELD_TYPES.SINGLE_SIGNATURE ||
    type === Constants.enums.FIELD_TYPES.ATTENDEE_SIGNATURES
  );
}

function buildMockResponse(template, pagesById) {
  if (!template?.id) return {};

  const mockPage = (page) => ({
    ...page,
    fields: (page.fields ?? []).map((row) => ({
      ...row,
      group: (row.group ?? [])
        .filter(isMockedField)
        .map((field) => ({ ...field, value: mockFieldValue(field) }))
    }))
  });

  const attachPages = (refs) =>
    (refs ?? []).map((ref) => {
      const page = pagesById?.[ref?.page_id];
      return {
        ...ref,
        ...(page ? { pageObj: mockPage(Helper.deepCopy(page)) } : {}),
        ...(Array.isArray(ref?.list_items)
          ? { list_items: attachPages(ref.list_items) }
          : {})
      };
    });

  return Helper.generateFormTemplateResponse(
    { ...Helper.deepCopy(template), pages: attachPages(template.pages) },
    true
  );
}

function isEnteredValue(value) {
  if (value === null || value === undefined || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function findEnteredValue(node, key) {
  if (!node || typeof node !== "object") return undefined;
  if (
    !Array.isArray(node) &&
    Object.prototype.hasOwnProperty.call(node, key) &&
    isEnteredValue(node[key])
  ) {
    return node[key];
  }
  for (const child of Object.values(node)) {
    const found = findEnteredValue(child, key);
    if (found !== undefined) return found;
  }
  return undefined;
}

function mergeLiveIntoMock(mock, live, pagesById) {
  const fieldIds = new Set(Object.keys(buildFieldMap(pagesById)));

  const merge = (node) => {
    const out = {};
    for (const [key, value] of Object.entries(node ?? {})) {
      const isContainer =
        value !== null &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        !fieldIds.has(key);
      if (isContainer) {
        out[key] = merge(value);
        continue;
      }
      const entered = findEnteredValue(live, key);
      out[key] = entered !== undefined ? Helper.deepCopy(entered) : value;
    }
    return out;
  };

  return merge(mock);
}

/**
 * The form builder's preview response: the mock built from the template's
 * definition, overlaid with whatever the author typed into the live preview.
 * The whole `{ [templateId]: … }` object — callers that want the form's own
 * slot index it themselves.
 *
 * @param {Object} template           formBuilderStore.template
 * @param {Object} pagesById          formBuilderStore.pages
 * @param {Object} liveResponses      formTemplateResponseStore.response
 */
export function buildPreviewResponse(template, pagesById, liveResponses) {
  return mergeLiveIntoMock(
    buildMockResponse(template, pagesById),
    liveResponses?.[template?.id] ?? {},
    pagesById
  );
}

/**
 * The variable palette for a form, from its latest response.
 *
 * A form with no responses yields none — `hasResponse: false` is the signal to
 * tell the author to submit one rather than render an empty list.
 *
 * @param {string} formTemplateId
 * @returns {Promise<{variables: Array, pageTags: Object, hasResponse: boolean, error: string|null}>}
 */
export async function getResponseVariables(formTemplateId) {
  if (!Helper.isStringValueExist(formTemplateId)) {
    return {
      variables: [],
      pageTags: {},
      hasResponse: false,
      error: "Form template ID is required"
    };
  }

  try {
    const [{ template, pagesById }, response] = await Promise.all([
      Helper.loadFormPages(formTemplateId),
      useFormTemplateResponseStore().getLatestResponse(formTemplateId)
    ]);

    if (!template) {
      return {
        variables: [],
        pageTags: {},
        hasResponse: false,
        error: "Form template not found"
      };
    }

    const fieldMap = buildFieldMap(pagesById);
    return {
      variables: response ? buildResponseVariables(response, fieldMap) : [],
      pageTags: buildPageTagMap(template),
      hasResponse: Boolean(response),
      hasFields: hasDataFields(fieldMap),
      error: null
    };
  } catch (error) {
    console.error("Error loading response variables:", error);
    return {
      variables: [],
      pageTags: {},
      hasResponse: false,
      error: error?.message || "Failed to load form variables"
    };
  }
}
